import { createFileRoute, redirect } from '@tanstack/react-router';
import { getActiveBrandId } from '../lib/active-brand.js';
import { billingQuery, brandsQuery, meQuery } from '../lib/queries.js';
import { PENDING_PLAN_STORAGE_KEY } from './start.js';

// `/home` — bookmark-compat redirector. Post-URL-restructure, the
// per-brand home lives at `/brand/$slug/home`. Hitting flat `/home`
// resolves the founder's last-active brand (or the org's default) and
// bounces them there. Keeps old links + the post-login redirect from
// breaking.
//
// Once every page reads brand from the URL exclusively, this route
// could be retired or repurposed as the rollup view. For now it's
// just a graceful redirect.

export const Route = createFileRoute('/home')({
  // Pass-through validator for arbitrary search params. /home is a
  // pure redirector, but notification deep-links carry overlays
  // (?card=<id>, ?triage=…) that the per-brand /home route consumes.
  // Without validateSearch capturing them, TanStack would strip them
  // before we could re-inject into the redirect (54h.27 — notification
  // CTAs sent the founder to /home but the recap drawer never opened
  // because the ?card= param was dropped). Loose-typed because the
  // strict shape is the destination route's concern, not ours.
  validateSearch: (search: Record<string, unknown>): Record<string, string> => {
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(search)) {
      if (typeof v === 'string' && v.length > 0) out[k] = v;
    }
    return out;
  },
  beforeLoad: async ({ context: { queryClient }, search }) => {
    const me = await queryClient.ensureQueryData(meQuery());
    if (!me) throw redirect({ to: '/login' });

    // [designer-role] Designer-only users — their workspace has none
    // of /home's surfaces wired for them (no campaigns, no analytics,
    // no channel control). Bounce straight to /designer where their
    // assigned-slot queue lives. Designer + editor + manager etc.
    // (mixed) keeps the normal flow.
    if (me.designerOnly) throw redirect({ to: '/designer' });

    // s120 / s200 — post-login resumption of a landing-page deep-link.
    // If the founder clicked Founder Pack / Growth on the landing
    // while logged out, /start stashed the plan key in sessionStorage.
    // Pick it up here and route to the right destination based on
    // whether they already have an active subscription (upgrade flow →
    // /settings/billing) vs are still onboarding (→ /onboarding/plan).
    // Clear the stash either way so a refresh doesn't re-loop.
    let pendingPlan: 'founder_pack' | 'growth' | null = null;
    if (typeof window !== 'undefined') {
      try {
        const stored = window.sessionStorage.getItem(PENDING_PLAN_STORAGE_KEY);
        if (stored === 'founder_pack' || stored === 'growth') {
          pendingPlan = stored;
          window.sessionStorage.removeItem(PENDING_PLAN_STORAGE_KEY);
        }
      } catch {
        // sessionStorage can throw in private browsing — silent fallback
        // means they land on /home normally; they can re-navigate to
        // billing manually if they meant to.
      }
    }
    if (pendingPlan) {
      const billing = await queryClient.ensureQueryData(billingQuery());
      const isActiveSubscriber =
        billing.subscriptionStatus === 'active' || billing.subscriptionStatus === 'trialing';
      if (isActiveSubscriber) {
        throw redirect({
          to: '/settings/billing',
          search: { prompt: pendingPlan },
        });
      }
      // Not yet subscribed — value-first reorder: DON'T jump them at the
      // paywall now (a fresh deep-link user has no brand yet, and the
      // paywall lives at the end of brand onboarding). Re-stash the plan
      // key and fall through to the normal flow below (→ welcome →
      // create brand → onboarding). The end-of-onboarding plan picker
      // reads the same key and pre-selects what they clicked.
      try {
        window.sessionStorage.setItem(PENDING_PLAN_STORAGE_KEY, pendingPlan);
      } catch {
        // silent — picker will just default to founder_pack.
      }
      // intentional fall-through (no redirect)
    }

    const brands = await queryClient.ensureQueryData(brandsQuery());
    const live = brands.filter((b) => b.archivedAt === null);
    if (live.length === 0) {
      // No live brand — either a fresh signup (never paid, never named
      // anything) or the founder archived/deleted everything.
      //
      // [12.2L-55] If subscriptionStatus is still 'none', this is a
      // fresh signup that hasn't been through onboarding yet — route
      // to /onboarding/welcome so they get the workspace-name step +
      // mag's intro. Without this, Google-OAuth signups land here
      // (better-auth's callbackURL doesn't always survive the round
      // trip cleanly) and skip the entire onboarding flow.
      //
      // Otherwise (had a plan/trial at some point, all brands gone)
      // /brands is the right surface — create-brand form, no need to
      // re-pitch the welcome screen.
      const billing = await queryClient.ensureQueryData(billingQuery());
      if (billing.subscriptionStatus === 'none') {
        throw redirect({ to: '/onboarding/welcome' });
      }
      throw redirect({ to: '/brands' });
    }
    const activeId = getActiveBrandId();
    const target = live.find((b) => b.id === activeId) ?? live[0];
    if (!target) throw redirect({ to: '/brands' });

    // Value-first routing: don't push an unpaid founder at the (gated)
    // paid home. If their brand still has onboarding to run, resume it —
    // that free preview is what earns the payment. If they've already
    // been through it and bailed at the paywall, bring them back to it.
    // Paid founders fall straight through to their home as before.
    const billing = await queryClient.ensureQueryData(billingQuery());
    const paid =
      billing.subscriptionStatus === 'active' || billing.subscriptionStatus === 'trialing';
    if (!paid) {
      if (!target.onboardedAt) {
        throw redirect({ to: '/brand/$slug/onboarding/url', params: { slug: target.slug } });
      }
      throw redirect({ to: '/onboarding/plan', search: { brand: target.slug } });
    }

    throw redirect({
      to: '/brand/$slug/home',
      params: { slug: target.slug },
      // Forward whatever overlay params came in (?card / ?triage /
      // ?draft / ?brand / ?brandTab) so notification deep-links and
      // shared bookmarks resolve to the same view the originator saw.
      search,
    });
  },
  component: () => null,
});
